import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CarIcon, UserCircleIcon } from '@phosphor-icons/react';
import {
    HorizontalScrollFade,
    ScrollEndFade,
    SearchInput,
    SegmentedControl,
    Tabs,
} from '@/components';
import { Button } from '@/components/ui/Button';
import {
    IdentityAvatarGrid,
    type UndefinedIdentityCellConfig,
} from '@/components/IdentityAvatarGrid';
import { Popover } from '@/components/Popover';
import type { ActionMenuItem } from '@/components/EntityActionsMenu/EntityActionsMenu';
import { Tag } from '@/components/Tag';
import type { EntityKind, Identity, IdentityToken } from '@/types/identity';
import { IdentityKnownFilter } from '@/types/identity';
import { usePopoverCloseSignal } from '@/stores';
import { buildUndefinedIdentityCell } from '@/utils/identity';
import {
    collectIdentityTags,
    filterIdentities,
} from './utils/filterIdentities';

const GRID_COLUMNS = 4;

interface IdentityFilterPanelProps {
    identities: Identity[];
    selectedTokens: Set<IdentityToken>;
    onToggle: (token: IdentityToken) => void;
    onClear: () => void;
    undefinedEventCounts: Record<EntityKind, number>;
    buildMenuItems: (identity: Identity) => ActionMenuItem[];
}

export function IdentityFilterPanel({
    identities,
    selectedTokens,
    onToggle,
    onClear,
    undefinedEventCounts,
    buildMenuItems,
}: IdentityFilterPanelProps) {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [kind, setKind] = useState<EntityKind>('person');
    const [knownFilter, setKnownFilter] = useState(IdentityKnownFilter.ALL);
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [tagRow, setTagRow] = useState<HTMLDivElement | null>(null);
    const closePopover = usePopoverCloseSignal((state) => state.trigger);

    const ofKind = identities.filter((identity) => identity.kind === kind);
    const visibleIdentities = filterIdentities({
        identities,
        kind,
        search,
        knownFilter,
        selectedTags,
    });
    const availableTags = collectIdentityTags(ofKind);
    const showTags =
        knownFilter !== IdentityKnownFilter.UNKNOWN && availableTags.length > 0;
    const undefinedCount = undefinedEventCounts[kind];
    const undefinedCells: UndefinedIdentityCellConfig[] =
        undefinedCount > 0
            ? [
                  buildUndefinedIdentityCell({
                      kind,
                      count: undefinedCount,
                      t,
                      labelPerKind: false,
                  }),
              ]
            : [];

    const toggleTag = (tag: string) => {
        const next = new Set(selectedTags);
        if (next.has(tag)) {
            next.delete(tag);
        } else {
            next.add(tag);
        }
        setSelectedTags(next);
    };

    return (
        <Popover
            title={t('identityFilter.title')}
            headerActions={
                <Button
                    variant="tertiary"
                    onClick={onClear}
                    data-testid="identity-filter-clear"
                    className={selectedTokens.size === 0 ? 'invisible' : ''}
                >
                    {t('clear')}
                </Button>
            }
            onClose={closePopover}
            className="w-[480px]"
            data-testid="identity-filter-panel"
        >
            <SearchInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('identityFilter.searchPlaceholder')}
                data-testid="identity-filter-search"
                autoFocus
            />
            <div className="flex flex-col gap-4">
                <SegmentedControl<EntityKind>
                    value={kind}
                    onValueChange={(nextKind) => {
                        setKind(nextKind);
                        setSelectedTags(new Set());
                    }}
                    size="compact"
                    fullWidth
                    options={[
                        {
                            value: 'person',
                            label: t('identityFilter.persons'),
                            icon: <UserCircleIcon className="size-4" />,
                        },
                        {
                            value: 'vehicle',
                            label: t('identityFilter.vehicles'),
                            icon: <CarIcon className="size-4" />,
                        },
                    ]}
                />
                <Tabs
                    activeTab={knownFilter}
                    onTabChange={(tabId) => {
                        const nextKnownFilter = tabId as IdentityKnownFilter;
                        setKnownFilter(nextKnownFilter);
                        if (nextKnownFilter === IdentityKnownFilter.UNKNOWN) {
                            setSelectedTags(new Set());
                        }
                    }}
                    tabs={[
                        {
                            id: IdentityKnownFilter.ALL,
                            label: t('identityFilter.all'),
                            count: ofKind.length,
                        },
                        {
                            id: IdentityKnownFilter.UNKNOWN,
                            label: t('identityFilter.unknown'),
                            count: ofKind.filter((identity) => !identity.known)
                                .length,
                        },
                        {
                            id: IdentityKnownFilter.KNOWN,
                            label: t('identityFilter.known'),
                            count: ofKind.filter((identity) => identity.known)
                                .length,
                        },
                    ]}
                />
                {showTags && (
                    <div className="relative">
                        <div
                            ref={setTagRow}
                            className="flex gap-2 overflow-x-auto scrollbar-hide"
                        >
                            {availableTags.map((tag) => (
                                <Tag
                                    key={tag}
                                    variant="outline"
                                    selected={selectedTags.has(tag)}
                                    onClick={() => toggleTag(tag)}
                                >
                                    {tag}
                                </Tag>
                            ))}
                        </div>
                        <HorizontalScrollFade
                            container={tagRow}
                            contentVersions={[availableTags.join(',')]}
                            className="from-grey-600 to-[rgba(27,28,30,0)]"
                        />
                    </div>
                )}
                <div className="relative">
                    <div
                        ref={setScrollContainer}
                        className="-mx-1.5 h-[max(340px,calc(100dvh-600px))] overflow-y-auto p-1.5 scrollbar-grey-400"
                    >
                        <IdentityAvatarGrid
                            identities={visibleIdentities}
                            selectedTokens={selectedTokens}
                            onToggle={onToggle}
                            buildMenuItems={buildMenuItems}
                            columns={GRID_COLUMNS}
                            showLabels
                            size="xl"
                            undefinedCells={undefinedCells}
                            emptyLabel={t(
                                kind === 'person'
                                    ? 'identityFilter.emptyPersons'
                                    : 'identityFilter.emptyVehicles',
                            )}
                        />
                    </div>
                    <ScrollEndFade
                        container={scrollContainer}
                        contentVersions={[visibleIdentities.length]}
                        className="mr-3"
                    />
                </div>
            </div>
        </Popover>
    );
}
